import { ErrorEvent, EventHint } from '@sentry/angular';
import {
  Event as ServerEvent,
  EventHint as ServerEventHint,
} from '@sentry/node';

/**
 * List of Regular Expressions to filter out errors we don't want sent to Sentry, using error text.
 */
const SAJ_EVENT_MESSAGE_FILTERS = [
  new RegExp(
    /edge\/login\/(?!null).*(401|405) (Unauthorized|Method Not Allowed)$/,
    'i',
  ), // Generated when session token is expired.
  new RegExp(/edge\/login\/(?!null).*401\s*OK$/, 'i'), // Also can be generated when session token is expired.
  new RegExp(/401.*OK.*edge\/login\/(?!null)/), // downstream error of login 401 - https://sentry.io/organizations/snag/issues/2430593085/?environment=master&project=5397074&query=is%3Aunresolved&sort=freq&statsPeriod=14d
];

/**
 * Takes in an event and checks if the error type is matched by any regex in the provided list.
 */
function isFilteredErrorValue(
  event: ErrorEvent | ServerEvent,
  types: Array<RegExp>,
): boolean {
  const eventValues = event.exception?.values
    ?.filter((y) => y.value)
    .map((x) => x.value);

  return (
    eventValues !== undefined &&
    eventValues.find((x) => types.some((y) => x.match(y))) !== undefined
  );
}

function isFilteredSerializedName(serialized: any): boolean {
  //HttpErrorResponse is logged twice. This finds the one logged by TryCatch integration
  return serialized?.name?.toLowerCase() === 'httperrorresponse';
}
/**
 * Checks for and filters out Event sends for Sentry.
 *
 * @remarks
 *  See documentation: https://docs.sentry.io/platforms/javascript/config/filter/#using-before-send
 */
export function filterOutEventsBeforeSend(
  event: ErrorEvent,
  _?: EventHint,
): ErrorEvent {
  const shouldFilter =
    isFilteredErrorValue(event, SAJ_EVENT_MESSAGE_FILTERS) ||
    isFilteredSerializedName(event?.extra?.__serialized__);

  return shouldFilter ? null : event;
}

export function filterOutServerEventsBeforeSend(
  event: ServerEvent,
  _: ServerEventHint,
): ServerEvent {
  const shouldFilter =
    isFilteredErrorValue(event, SAJ_EVENT_MESSAGE_FILTERS) ||
    isFilteredSerializedName(event?.extra?.__serialized__);

  return shouldFilter ? null : event;
}
